// TranscriptLite engine — Web Speech API for live mic + Groq Whisper for file upload.

export interface TranscriptSegment {
  id: string;
  speaker: string;
  startSec: number;
  endSec: number;
  text: string;
}

export interface TranscriptSession {
  id: string;
  title: string;
  createdISO: string;
  language: string;
  segments: TranscriptSegment[];
  source: 'live' | 'upload';
  audioFileName?: string;
}

export function emptySession(): TranscriptSession {
  return { id: crypto.randomUUID(), title: 'Untitled session', createdISO: new Date().toISOString(), language: 'en-US', segments: [], source: 'live' };
}

export function formatTimestamp(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0 ? `${h}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}` : `${m}:${s.toString().padStart(2,'0')}`;
}

export function exportSRT(session: TranscriptSession): string {
  const fmt = (sec: number) => { const h = Math.floor(sec/3600), m = Math.floor((sec%3600)/60), s = Math.floor(sec%60), ms = Math.floor((sec%1)*1000); return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')},${ms.toString().padStart(3,'0')}`; };
  return session.segments.map((seg, i) => `${i+1}\n${fmt(seg.startSec)} --> ${fmt(seg.endSec)}\n${seg.speaker}: ${seg.text}\n`).join('\n');
}

export function exportVTT(session: TranscriptSession): string {
  const fmt = (sec: number) => { const h = Math.floor(sec/3600), m = Math.floor((sec%3600)/60), s = Math.floor(sec%60), ms = Math.floor((sec%1)*1000); return `${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}.${ms.toString().padStart(3,'0')}`; };
  return 'WEBVTT\n\n' + session.segments.map(seg => `${fmt(seg.startSec)} --> ${fmt(seg.endSec)}\n<v ${seg.speaker}>${seg.text}\n`).join('\n');
}

export function exportPlainText(session: TranscriptSession): string {
  return `# ${session.title}\n${new Date(session.createdISO).toLocaleString()}\n\n` + session.segments.map(seg => `[${formatTimestamp(seg.startSec)}] **${seg.speaker}:** ${seg.text}`).join('\n');
}

// Groq Whisper for file upload (uses buyer's key from useGroqKey)
export async function transcribeWithGroq(audioBlob: Blob, language: string, apiKey: string): Promise<{ segments: TranscriptSegment[]; text: string }> {
  const form = new FormData();
  form.append('file', audioBlob, 'audio.webm');
  form.append('model', 'whisper-large-v3-turbo');
  form.append('language', language.split('-')[0]);
  form.append('response_format', 'verbose_json');
  form.append('timestamp_granularities[]', 'segment');

  const res = await fetch('https://api.groq.com/openai/v1/audio/transcriptions', {
    method: 'POST',
    headers: { Authorization: `Bearer ${apiKey}` },
    body: form,
  });
  if (!res.ok) throw new Error(`Groq Whisper error ${res.status}: ${await res.text()}`);
  const data = await res.json() as { segments?: Array<{ id: number; start: number; end: number; text: string }>; text: string };
  const segments: TranscriptSegment[] = (data.segments || []).map(s => ({
    id: crypto.randomUUID(),
    speaker: 'Speaker 1',
    startSec: s.start,
    endSec: s.end,
    text: s.text.trim(),
  }));
  return { segments, text: data.text };
}

export interface SpeechRecognitionShim {
  start: () => void;
  stop: () => void;
  abort: () => void;
}

export function createLiveRecognizer(language: string, onResult: (text: string, isFinal: boolean) => void, onError: (msg: string) => void): SpeechRecognitionShim | null {
  const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!SpeechRec) return null;
  const r = new SpeechRec();
  r.lang = language;
  r.continuous = true;
  r.interimResults = true;
  r.onresult = (e: any) => {
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const text = e.results[i][0].transcript;
      onResult(text, e.results[i].isFinal);
    }
  };
  r.onerror = (e: any) => onError(e.error || 'recognition error');
  return r;
}
